import { useState, useCallback, useEffect } from "react";
import "./RecruiterDashboard.css";
import { getRecruitmentList } from "./Service/RecruitmentService";

/* ─────────────────────────────────────────────
   Toast helper (portal-less, appended to body)
───────────────────────────────────────────── */
function showToast(msg, type) {
  const t = document.createElement("div");
  t.className = "og-toast";
  const color = type === "warn" ? "#fbbf24" : "#4ade80";
  const icon = type === "warn" ? "fa-triangle-exclamation" : "fa-check-circle";
  t.innerHTML = `<i class="fa-solid ${icon}" style="color:${color};"></i>${msg}`;
  document.body.appendChild(t);
  setTimeout(() => {
    t.style.opacity = "0";
    t.style.transition = "opacity .3s";
    setTimeout(() => t.remove(), 300);
  }, 2800);
}

/* ─────────────────────────────────────────────
   Small data helpers
   (agar API se array/key ka data na mile to
   static/dummy fallback use hoga)
───────────────────────────────────────────── */
const hasArr = (arr) => Array.isArray(arr) && arr.length > 0;
const num = (val, fallback) =>
  val === undefined || val === null ? fallback : val;

/* ─────────────────────────────────────────────
   Rating Row (10-button scorer)
───────────────────────────────────────────── */
function RatingRow({ label }) {
  const [score, setScore] = useState(0);
  return (
    <div>
      <div style={{ fontSize: ".78rem", fontWeight: 700, marginBottom: ".3rem" }}>
        {label}
      </div>
      <div style={{ display: "flex", gap: ".2rem" }}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            className={`rating-btn${score >= n ? " active" : ""}`}
            onClick={() => setScore(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Feedback Modal
───────────────────────────────────────────── */
function FeedbackModal({ candidateName, onClose }) {
  const [rec, setRec] = useState("");

  const handleSubmit = () => {
    onClose();
    showToast("Feedback submitted successfully");
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box">
        {/* Header */}
        <div
          style={{
            padding: "1rem 1.25rem",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: ".65rem",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: "#fffbeb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="fa-solid fa-star" style={{ color: "var(--amber)" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: ".92rem", fontWeight: 700 }}>
              Feedback — {candidateName}
            </div>
            <div style={{ fontSize: ".76rem", color: "var(--muted)" }}>
              Submit interview feedback
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "#f8f9fa",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--muted)",
            }}
          >
            <i className="fa-solid fa-times" style={{ fontSize: ".8rem" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "1rem 1.25rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: ".75rem",
              marginBottom: ".75rem",
            }}
          >
            <RatingRow label="Technical Skills" />
            <RatingRow label="Communication" />
            <RatingRow label="Problem Solving" />
            <RatingRow label="Culture Fit" />
          </div>

          <div style={{ marginBottom: ".75rem" }}>
            <label
              style={{
                fontSize: ".78rem",
                fontWeight: 700,
                display: "block",
                marginBottom: ".35rem",
              }}
            >
              Overall Recommendation
            </label>
            <div style={{ display: "flex", gap: ".75rem" }}>
              {[
                { val: "proceed", label: "Proceed", color: "var(--green)" },
                { val: "maybe", label: "Maybe", color: "var(--amber)" },
                { val: "reject", label: "Reject", color: "var(--red)" },
              ].map(({ val, label, color }) => (
                <label
                  key={val}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: ".35rem",
                    cursor: "pointer",
                    fontSize: ".85rem",
                  }}
                >
                  <input
                    type="radio"
                    name="rec"
                    value={val}
                    checked={rec === val}
                    onChange={() => setRec(val)}
                    style={{ accentColor: color }}
                  />
                  <span style={{ color, fontWeight: 600 }}>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <textarea
            style={{
              width: "100%",
              border: "1.5px solid var(--border)",
              borderRadius: 8,
              padding: ".5rem .8rem",
              fontSize: ".875rem",
              fontFamily: "inherit",
              outline: "none",
              resize: "vertical",
              minHeight: 70,
              lineHeight: 1.6,
            }}
            placeholder="Comments…"
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: ".85rem 1.25rem",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: ".55rem",
          }}
        >
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit}>
            <i className="fa-solid fa-check" /> Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STATIC FALLBACK DATA
   (jab tak API se related data na mile, ye dikhega)
───────────────────────────────────────────── */
const STATIC = {
  greeting: { interviews: 3, feedback: 4 },
  kpis: {
    OpenJobs: 14,
    ActiveCandidates: 148,
    TodayInterviews: 3,
    FeedbackPending: 4,
    OffersPending: 2,
    TRApprovalsNeeded: 5,
  },
  todayInterviews: [
    {
      Time: "11:00",
      AmPm: "AM",
      StripeColor: "var(--primary)",
      CandidateName: "Rahul Kumar",
      Round: "R2 — HR Interview",
      Mode: "Meet",
      ModeIcon: "fa-video",
      JobCode: "JOB-2026-001",
      Panel: [{ Initials: "PS", Color: "#EC4119" }],
      Status: "feedback",
    },
    {
      Time: "2:00",
      AmPm: "PM",
      StripeColor: "var(--blue)",
      CandidateName: "Amit Kumar",
      Round: "R1 — Technical",
      Mode: "Zoom",
      ModeIcon: "fa-video",
      JobCode: "JOB-2026-001",
      Panel: [
        { Initials: "RD", Color: "#0891b2" },
        { Initials: "NG", Color: "#7c3aed" },
      ],
      Status: "Upcoming",
      StatusBg: "var(--amber-lt)",
      StatusColor: "var(--amber)",
    },
    {
      Time: "4:00",
      AmPm: "PM",
      StripeColor: "var(--green)",
      CandidateName: "Priya Singh",
      Round: "R3 — Director Round",
      Mode: "In-person",
      ModeIcon: "fa-building",
      JobCode: "JOB-2026-001",
      Panel: [{ Initials: "AM", Color: "#16a34a" }],
      Status: "Cleared",
      StatusBg: "var(--green-lt)",
      StatusColor: "var(--green)",
    },
  ],
  activeJobs: [
    {
      Title: "Senior Frontend Developer",
      Code: "JOB-2026-001",
      Dept: "Engineering",
      AgingLabel: "25d open",
      AgingBg: "#fef9c3",
      AgingColor: "#854d0e",
      Segments: [
        { width: 36, color: "#6366f1", title: "Applied: 5" },
        { width: 22, color: "var(--amber)", title: "Interview: 6" },
        { width: 16, color: "var(--green)", title: "Shortlisted: 4" },
        { width: 8, color: "#7c3aed", title: "Offer: 2" },
      ],
      Total: 18,
      Status: "Open",
      StatusClass: "badge-open",
    },
    {
      Title: "Data Analyst – BI",
      Code: "JOB-2026-004",
      Dept: "Analytics",
      AgingLabel: "18d open",
      AgingBg: "#fef9c3",
      AgingColor: "#854d0e",
      Segments: [
        { width: 28, color: "#6366f1", title: "Applied: 4" },
        { width: 20, color: "var(--amber)", title: "Interview: 5" },
        { width: 10, color: "var(--green)", title: "Shortlisted: 2" },
      ],
      Total: 12,
      Status: "Open",
      StatusClass: "badge-open",
    },
    {
      Title: "HR Business Partner",
      Code: "JOB-2026-003",
      Dept: "HR",
      AgingLabel: "37d open",
      AgingBg: "var(--amber-lt)",
      AgingColor: "var(--amber)",
      Segments: [
        { width: 18, color: "#6366f1", title: "Applied: 3" },
        { width: 10, color: "var(--amber)", title: "Interview: 2" },
      ],
      Total: 5,
      Status: "On Hold",
      StatusClass: "badge-hold",
    },
    {
      Title: "Senior Backend Engineer",
      Code: "JOB-2026-005",
      Dept: "Engineering",
      AgingLabel: "16d open",
      AgingBg: "#fef9c3",
      AgingColor: "#854d0e",
      Segments: [
        { width: 22, color: "#6366f1", title: "Applied: 4" },
        { width: 14, color: "var(--amber)", title: "Interview: 3" },
      ],
      Total: 7,
      Status: "Open",
      StatusClass: "badge-open",
    },
  ],
  pendingActions: [
    {
      IconBg: "var(--amber-lt)",
      Icon: "fa-star",
      IconColor: "var(--amber)",
      Title: "Feedback overdue — 2 interviews",
      Sub: "Neha Kapoor (2d) · Meera Joshi (4d)",
      ActionLabel: "Review",
      ActionType: "navigate",
      ActionTarget: "recruitment-interviews.html",
    },
    {
      IconBg: "#f0fdf4",
      Icon: "fa-file-contract",
      IconColor: "var(--green)",
      Title: "Offer pending acceptance — Neha Kapoor",
      Sub: "Sent 3 days ago · Awaiting response",
      ActionLabel: "fa-bell",
      ActionType: "toast",
      ActionTarget: "Reminder sent to Neha Kapoor",
    },
    {
      IconBg: "var(--blue-lt)",
      Icon: "fa-clipboard-list",
      IconColor: "var(--blue)",
      Title: "5 Talent Requests awaiting approval",
      Sub: "Oldest: TR-2026-003 · 12 days pending",
      ActionLabel: "Review",
      ActionType: "navigate",
      ActionTarget: "recruitment-talent-requests.html",
    },
    {
      IconBg: "var(--purple-lt)",
      Icon: "fa-user-plus",
      IconColor: "var(--purple)",
      Title: "2 candidates need stage update",
      Sub: "Stale for 5+ days after last interview",
      ActionLabel: "View",
      ActionType: "navigate",
      ActionTarget: "recruitment-candidates.html",
    },
    {
      IconBg: "var(--green-lt)",
      Icon: "fa-user-check",
      IconColor: "var(--green)",
      Title: "Create employee — Neha Kapoor hired",
      Sub: "Offer accepted · Employee record pending",
      ActionLabel: "fa-user-plus",
      ActionType: "toast",
      ActionTarget: "Opening employee creation form…",
    },
    {
      IconBg: "var(--primary-lt)",
      Icon: "fa-triangle-exclamation",
      IconColor: "var(--primary)",
      Title: "Job aging alert — JOB-2026-003",
      Sub: "HR Business Partner · On Hold · 37 days",
      ActionLabel: "View",
      ActionType: "navigate",
      ActionTarget: "recruitment-job-detail.html",
      Highlight: true,
    },
  ],
  stageCounts: [
    { count: 18, label: "Applied", bg: "#f0f9ff", color: "#0369a1" },
    { count: 11, label: "Interview", bg: "#fff7ed", color: "#c2410c" },
    { count: 6, label: "Shortlisted", bg: "var(--green-lt)", color: "var(--green)" },
    { count: 2, label: "Offer/Hired", bg: "#f0fdf4", color: "#14532d" },
  ],
  funnel: [
    { label: "Applied", pct: "100%", color: "#6366f1", count: 148 },
    { label: "Screening", pct: "68%", color: "#0891b2", count: 101 },
    { label: "Interview", pct: "40%", color: "var(--amber)", count: 59 },
    { label: "Shortlisted", pct: "18%", color: "var(--green)", count: 27 },
    { label: "Offer Given", pct: "9%", color: "var(--primary)", count: 13 },
    { label: "Hired", pct: "5%", color: "#15803d", count: 8 },
  ],
  weekInterviews: [
    { count: 3, day: "Mon", isToday: false },
    { count: 2, day: "Tue", isToday: false },
    { count: 3, day: "Today", isToday: true },
    { count: 2, day: "Fri", isToday: false },
    { count: 1, day: "Sat", isToday: false },
  ],
  weekTotal: 11,
  offersInFlight: [
    {
      Initials: "NK",
      AvatarColor: "#7c3aed",
      Name: "Neha Kapoor",
      Sub: "Sr. Frontend Dev · Sent 3 days ago",
      Bg: "#f0fdf4",
      Border: "1px solid #86efac",
      StatusLabel: "Awaiting",
      StatusBg: "#fef9c3",
      StatusColor: "#854d0e",
      Overdue: false,
      ToastMsg: "Reminder sent to Neha Kapoor",
    },
    {
      Initials: "MJ",
      AvatarColor: "#6366f1",
      Name: "Meera Joshi",
      Sub: "Product Manager · Sent 6 days ago",
      Bg: "#fff7ed",
      Border: "1px solid #fdba74",
      StatusLabel: "Overdue",
      StatusBg: "var(--red-lt)",
      StatusColor: "var(--red)",
      Overdue: true,
      ToastMsg: "Urgent reminder sent to Meera Joshi",
    },
  ],
  activityLeft: [
    {
      Dot: "var(--green)",
      Icon: "fa-check",
      Html: ["Priya Singh", " — Interview R3 cleared"],
      Time: "Today 4:00 PM",
    },
    {
      Dot: "var(--primary)",
      Icon: "fa-paper-plane",
      Html: ["Offer sent", " to Neha Kapoor · Sr. Frontend Dev"],
      Time: "Today 11:30 AM",
    },
    {
      Dot: "#6366f1",
      Icon: "fa-arrow-right",
      Html: ["Rahul Kumar", " moved to Interview stage"],
      Time: "Yesterday 3:15 PM",
    },
    {
      Dot: "var(--blue)",
      Icon: "fa-user-plus",
      Html: ["New candidate", " Siddharth Rao added via ATS"],
      Time: "Yesterday 10:00 AM",
    },
  ],
  activityRight: [
    {
      Dot: "var(--amber)",
      Icon: "fa-pause",
      Html: ["JOB-2026-003", " HR Business Partner put on hold"],
      Time: "Jun 24 2:00 PM",
    },
    {
      Dot: "var(--red)",
      Icon: "fa-xmark",
      Html: ["Vikash Roy", " rejected after R1 · Skills mismatch"],
      Time: "Jun 22 5:00 PM",
    },
    {
      Dot: "var(--purple)",
      Icon: "fa-briefcase",
      Html: ["JOB-2026-007", " created · UX Designer"],
      Time: "Jun 21 9:00 AM",
    },
    {
      Dot: "var(--green)",
      Icon: "fa-check",
      Html: ["TR-2026-015", " approved · Backend Engineer"],
      Time: "Jun 20 4:30 PM",
    },
  ],
  monthStats: {
    CandidatesAdded: 24,
    InterviewsScheduled: 18,
    OffersGiven: 3,
    HiresConfirmed: 2,
    AvgDaysToOffer: "22d",
  },
};

/* ─────────────────────────────────────────────
   Main Dashboard Component
───────────────────────────────────────────── */
export default function RecruiterDashboard() {
  const [feedbackModal, setFeedbackModal] = useState(null); // null | candidate name string
  const [recruitmentList, setRecruitmentList] = useState(null); // API Data object

  const navigate = useCallback((href) => {
    window.location.href = href;
  }, []);

  useEffect(() => {
    GetRecruitmentList();
  }, []);

  async function GetRecruitmentList() {
    const  params = {
      ViewMode: 0,
    };

    try {
      let res = await getRecruitmentList(params);
      setRecruitmentList(res?.Data);
    } catch (error) {}
  }

  // shorthand for API data (agar null hai to khali object treat hoga)
  const d = recruitmentList || {};

  /* ---------- KPIs ---------- */
  const kpis = {
    OpenJobs: num(d.KPIs?.OpenJobs, STATIC.kpis.OpenJobs),
    ActiveCandidates: num(d.KPIs?.ActiveCandidates, STATIC.kpis.ActiveCandidates),
    TodayInterviews: num(d.KPIs?.TodayInterviews, STATIC.kpis.TodayInterviews),
    FeedbackPending: num(d.KPIs?.FeedbackPending, STATIC.kpis.FeedbackPending),
    OffersPending: num(d.KPIs?.OffersPending, STATIC.kpis.OffersPending),
    TRApprovalsNeeded: num(d.KPIs?.TRApprovalsNeeded, STATIC.kpis.TRApprovalsNeeded),
  };

  /* ---------- Today's Interviews ---------- */
  const todayInterviews = hasArr(d.TodayInterviews)
    ? d.TodayInterviews
    : STATIC.todayInterviews;

  /* ---------- Active Jobs ---------- */
  const activeJobs = hasArr(d.ActiveJobs) ? d.ActiveJobs : STATIC.activeJobs;

  /* ---------- Pending Actions ---------- */
  const pendingActions = hasArr(d.PendingActions)
    ? d.PendingActions
    : STATIC.pendingActions;

  /* ---------- Pipeline Funnel ---------- */
  const apiFunnel = hasArr(d.PipelineFunnel)
    ? d.PipelineFunnel.filter((s) => num(s.CandidateCount, 0) > 0)
    : [];
  const funnel =
    apiFunnel.length > 0
      ? d.PipelineFunnel.map((s, i) => ({
          label: s.Stage,
          count: s.CandidateCount,
          pct: `${Math.min(
            100,
            Math.round(
              (s.CandidateCount /
                (d.PipelineFunnel[0]?.CandidateCount || 1)) *
                100
            )
          )}%`,
          color: STATIC.funnel[i]?.color || "var(--primary)",
        }))
      : STATIC.funnel;
  const stageCounts = STATIC.stageCounts; // koi alag key nahi mili API me iske liye

  /* ---------- This Week's Interviews ---------- */
  const weekInterviews = hasArr(d.UpcomingInterviews)
    ? d.UpcomingInterviews
    : STATIC.weekInterviews;
  const weekTotal = num(
    hasArr(d.UpcomingInterviews)
      ? d.UpcomingInterviews.reduce((s, w) => s + num(w.count, 0), 0)
      : null,
    STATIC.weekTotal
  );

  /* ---------- Offers in Flight ---------- */
  const offersInFlight = hasArr(d.OffersInFlight)
    ? d.OffersInFlight
    : STATIC.offersInFlight;

  /* ---------- This Month ---------- */
  const monthStats = {
    CandidatesAdded: num(d.MonthStats?.CandidatesAdded, STATIC.monthStats.CandidatesAdded),
    InterviewsScheduled: num(
      d.MonthStats?.InterviewsScheduled,
      STATIC.monthStats.InterviewsScheduled
    ),
    OffersGiven: num(d.MonthStats?.OffersGiven, STATIC.monthStats.OffersGiven),
    HiresConfirmed: num(d.MonthStats?.HiresConfirmed, STATIC.monthStats.HiresConfirmed),
    AvgDaysToOffer: d.MonthStats?.AvgDaysToOffer ?? STATIC.monthStats.AvgDaysToOffer,
  };

  return (
    <>
      {/* ── Main Content ── */}
      {/* <div className="main"> */}
      {/* Topbar */}
      <div className="topbar">
        <i className="fa-solid fa-gauge" style={{ color: "var(--primary)" }} />
        <strong style={{ color: "var(--text)" }}>Recruitment Dashboard</strong>
        <div
          style={{
            fontSize: ".75rem",
            color: "var(--muted)",
            background: "var(--primary-lt)",
            border: "1px solid var(--red-border)",
            borderRadius: 20,
            padding: "2px 10px",
            marginLeft: ".5rem",
          }}
        >
          Friday, June 26, 2026
        </div>
        <div className="top-right">
          <select
            className="og-select"
            style={{ width: 160, padding: ".35rem .7rem", fontSize: ".78rem" }}
            onChange={(e) => showToast(`Switched to ${e.target.value} view`)}
          >
            <option>My Assignments</option>
            <option>All Recruiters</option>
            <option>Team Overview</option>
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <div className="top-avatar">AM</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: ".8rem" }}>
                Arun Mishra
              </div>
              <div style={{ fontSize: ".68rem", color: "var(--muted)" }}>
                HR Admin · Recruiter
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body">
        {/* ── Greeting ── */}
        <div className="greeting">
          <div className="greeting-text">
            <h2>Good morning, Arun 👋</h2>
            <p>
              You have{" "}
              <strong style={{ color: "#fff" }}>
                {kpis.TodayInterviews} interviews
              </strong>{" "}
              today and{" "}
              <strong style={{ color: "#fff" }}>
                {kpis.FeedbackPending} feedback
              </strong>{" "}
              submissions pending. Here's your recruitment snapshot.
            </p>
          </div>
          <div className="greeting-actions">
            <button
              className="btn btn-sm"
              style={{
                background: "rgba(255,255,255,.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,.18)",
              }}
              onClick={() => navigate("recruitment-cv-upload.html")}
            >
              <i className="fa-solid fa-cloud-arrow-up" /> Upload CV
            </button>
            <button
              className="btn btn-sm"
              style={{ background: "var(--primary)", color: "#fff" }}
              onClick={() => navigate("recruitment-interview-schedule.html")}
            >
              <i className="fa-solid fa-calendar-plus" /> Schedule Interview
            </button>
            <button
              className="btn btn-sm"
              style={{
                background: "rgba(255,255,255,.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,.18)",
              }}
              onClick={() => navigate("recruitment-job-create.html")}
            >
              <i className="fa-solid fa-plus" /> Create Job
            </button>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        <div className="kpi-strip">
          <div className="kpi" onClick={() => navigate("recruitment-jobs.html")}>
            <div className="kpi-val" style={{ color: "var(--primary)" }}>
              {kpis.OpenJobs}
            </div>
            <div className="kpi-lbl">Open Jobs</div>
            <div className="kpi-trend" style={{ color: "var(--green)" }}>
              <i
                className="fa-solid fa-arrow-up"
                style={{ fontSize: ".6rem" }}
              />{" "}
              2 this week
            </div>
          </div>

          <div
            className="kpi"
            onClick={() => navigate("recruitment-candidates.html")}
          >
            <div className="kpi-val" style={{ color: "var(--blue)" }}>
              {kpis.ActiveCandidates}
            </div>
            <div className="kpi-lbl">Total Candidates</div>
            <div className="kpi-trend" style={{ color: "var(--green)" }}>
              <i
                className="fa-solid fa-arrow-up"
                style={{ fontSize: ".6rem" }}
              />{" "}
              12 this week
            </div>
          </div>

          <div
            className="kpi"
            onClick={() => navigate("recruitment-interviews.html")}
          >
            <div className="kpi-val" style={{ color: "var(--amber)" }}>
              {kpis.TodayInterviews}
            </div>
            <div className="kpi-lbl">Today's Interviews</div>
            <div className="kpi-trend" style={{ color: "var(--muted)" }}>
              Next: 11:00 AM
            </div>
          </div>

          <div
            className="kpi"
            onClick={() => navigate("recruitment-interviews.html")}
          >
            <div className="kpi-val" style={{ color: "var(--red)" }}>
              {kpis.FeedbackPending}
            </div>
            <div className="kpi-lbl">Feedback Pending</div>
            <div className="kpi-trend" style={{ color: "var(--red)" }}>
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ fontSize: ".6rem" }}
              />{" "}
              2 overdue
            </div>
          </div>

          <div className="kpi">
            <div className="kpi-val" style={{ color: "var(--purple)" }}>
              {kpis.OffersPending}
            </div>
            <div className="kpi-lbl">Offers Pending</div>
            <div className="kpi-trend" style={{ color: "var(--muted)" }}>
              Awaiting acceptance
            </div>
          </div>

          <div
            className="kpi"
            onClick={() => navigate("recruitment-talent-requests.html")}
          >
            <div className="kpi-val" style={{ color: "var(--gray)" }}>
              {kpis.TRApprovalsNeeded}
            </div>
            <div className="kpi-lbl">TR Approvals</div>
            <div className="kpi-trend" style={{ color: "var(--amber)" }}>
              <i
                className="fa-solid fa-clock"
                style={{ fontSize: ".6rem" }}
              />{" "}
              Needs review
            </div>
          </div>
        </div>

        {/* ── Three-column Row ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          {/* Today's Interviews */}
          <div className="og-card">
            <div
              style={{
                padding: ".85rem 1rem",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  fontSize: ".9rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: ".45rem",
                }}
              >
                <i
                  className="fa-solid fa-circle"
                  style={{ color: "var(--primary)", fontSize: ".55rem" }}
                />{" "}
                Today's Interviews
              </h4>
              <button
                className="btn btn-primary btn-xs"
                onClick={() => navigate("recruitment-interview-schedule.html")}
              >
                <i className="fa-solid fa-plus" /> Schedule
              </button>
            </div>

            {todayInterviews.map((iv, idx) => (
              <div className="interview-card" key={idx}>
                <div className="int-time">
                  <div className="int-hour">{iv.Time}</div>
                  <div className="int-ampm">{iv.AmPm}</div>
                </div>
                <div
                  className="int-stripe"
                  style={{ background: iv.StripeColor }}
                />
                <div className="int-info">
                  <div
                    className="int-name"
                    onClick={() => navigate("recruitment-candidate-profile.html")}
                  >
                    {iv.CandidateName}
                  </div>
                  <div className="int-meta">
                    <span>{iv.Round}</span>
                    <span>
                      <i className={`fa-solid ${iv.ModeIcon}`} /> {iv.Mode}
                    </span>
                  </div>
                  <div className="int-meta" style={{ marginTop: 2 }}>
                    <span style={{ color: "var(--muted)" }}>{iv.JobCode}</span>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: ".3rem",
                  }}
                >
                  <div style={{ display: "flex", gap: 2 }}>
                    {(iv.Panel || []).map((p, pi) => (
                      <div
                        key={pi}
                        className="panel-av"
                        style={{ background: p.Color }}
                      >
                        {p.Initials}
                      </div>
                    ))}
                  </div>
                  {iv.Status === "feedback" ? (
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={() => setFeedbackModal(iv.CandidateName)}
                    >
                      <i className="fa-solid fa-star" /> Feedback
                    </button>
                  ) : (
                    <span
                      className="int-badge"
                      style={{
                        background: iv.StatusBg,
                        color: iv.StatusColor,
                      }}
                    >
                      {iv.Status}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <div
              style={{
                padding: ".65rem 1rem",
                borderTop: "1px solid var(--border-lt)",
              }}
            >
              <a
                href="recruitment-interviews.html"
                style={{
                  fontSize: ".78rem",
                  fontWeight: 600,
                  color: "var(--primary)",
                  textDecoration: "none",
                }}
              >
                View all interviews →
              </a>
            </div>
          </div>

          {/* My Active Jobs */}
          <div className="og-card">
            <div
              style={{
                padding: ".85rem 1rem",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                <i
                  className="fa-solid fa-briefcase"
                  style={{
                    color: "var(--primary)",
                    fontSize: ".82rem",
                    marginRight: ".35rem",
                  }}
                />
                My Active Jobs
              </h4>
              <button
                className="btn btn-outline btn-xs"
                onClick={() => navigate("recruitment-jobs.html")}
              >
                All Jobs
              </button>
            </div>

            {activeJobs.map((job, idx) => (
              <div
                className="job-row"
                key={idx}
                onClick={() => navigate("recruitment-job-detail.html")}
              >
                <div className="jr-left">
                  <div className="jr-title">{job.Title}</div>
                  <div className="jr-sub">
                    {job.Code} · {job.Dept} ·{" "}
                    <span
                      style={{
                        background: job.AgingBg,
                        color: job.AgingColor,
                        fontSize: ".68rem",
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontWeight: 600,
                      }}
                    >
                      {job.AgingLabel}
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: ".45rem",
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                    }}
                  >
                    {(job.Segments || []).map((seg, si) => (
                      <div
                        key={si}
                        className="pm-seg"
                        style={{ width: seg.width, background: seg.color }}
                        title={seg.title}
                      />
                    ))}
                    <span
                      style={{ fontSize: ".7rem", color: "var(--muted)", marginLeft: 3 }}
                    >
                      {job.Total} total
                    </span>
                  </div>
                </div>
                <span className={`badge ${job.StatusClass}`}>{job.Status}</span>
              </div>
            ))}

            <div
              style={{
                padding: ".65rem 1rem",
                borderTop: "1px solid var(--border-lt)",
              }}
            >
              <a
                href="recruitment-jobs.html"
                style={{
                  fontSize: ".78rem",
                  fontWeight: 600,
                  color: "var(--primary)",
                  textDecoration: "none",
                }}
              >
                View all {kpis.OpenJobs} open jobs →
              </a>
            </div>
          </div>

          {/* Pending Actions */}
          <div className="og-card">
            <div
              style={{
                padding: ".85rem 1rem",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                <i
                  className="fa-solid fa-circle-exclamation"
                  style={{
                    color: "var(--red)",
                    fontSize: ".82rem",
                    marginRight: ".35rem",
                  }}
                />
                Pending Actions
              </h4>
            </div>

            {pendingActions.map((act, idx) => (
              <div
                className="action-item recruiter"
                key={idx}
                style={act.Highlight ? { background: "#fff8f1" } : undefined}
              >
                <div className="ai-icon" style={{ background: act.IconBg }}>
                  <i
                    className={`fa-solid ${act.Icon}`}
                    style={{ color: act.IconColor }}
                  />
                </div>
                <div className="ai-text">
                  <div className="ai-title">{act.Title}</div>
                  <div className="ai-sub">{act.Sub}</div>
                </div>
                {act.ActionType === "navigate" ? (
                  <button
                    className="btn btn-outline btn-xs"
                    onClick={() => navigate(act.ActionTarget)}
                  >
                    {act.ActionLabel}
                  </button>
                ) : (
                  <button
                    className="btn btn-outline btn-xs"
                    onClick={() => showToast(act.ActionTarget)}
                  >
                    <i className={`fa-solid ${act.ActionLabel}`} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Second Row: Pipeline + Right Column ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          {/* Pipeline Summary */}
          <div className="og-card">
            <div
              style={{
                padding: ".85rem 1rem",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                <i
                  className="fa-solid fa-pipe-section"
                  style={{
                    color: "var(--primary)",
                    fontSize: ".82rem",
                    marginRight: ".35rem",
                  }}
                />
                Pipeline Summary
              </h4>
              <select
                className="og-select"
                style={{ width: 140, padding: ".3rem .6rem", fontSize: ".76rem" }}
              >
                <option>All my jobs</option>
                <option>JOB-2026-001</option>
                <option>JOB-2026-004</option>
                <option>JOB-2026-005</option>
              </select>
            </div>

            <div style={{ padding: "1rem" }}>
              {/* Stage Counts */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: ".6rem",
                  marginBottom: "1rem",
                }}
              >
                {stageCounts.map(({ count, label, bg, color }) => (
                  <div
                    key={label}
                    style={{
                      textAlign: "center",
                      padding: ".65rem .5rem",
                      background: bg,
                      borderRadius: 9,
                    }}
                  >
                    <div style={{ fontSize: "1.2rem", fontWeight: 700, color }}>
                      {count}
                    </div>
                    <div style={{ fontSize: ".68rem", color, fontWeight: 600 }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Funnel */}
              <div
                style={{
                  fontSize: ".74rem",
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                  marginBottom: ".6rem",
                }}
              >
                Conversion Funnel
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                {funnel.map(({ label, pct, color, count }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: ".65rem" }}
                  >
                    <span
                      style={{
                        fontSize: ".74rem",
                        color: "var(--text)",
                        minWidth: 90,
                      }}
                    >
                      {label}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        background: "var(--gray-lt)",
                        borderRadius: 99,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: pct,
                          height: "100%",
                          background: color,
                          borderRadius: 99,
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: ".74rem",
                        fontWeight: 700,
                        minWidth: 24,
                      }}
                    >
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: This Week + Offers */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* This Week's Interviews */}
            <div className="og-card">
              <div
                style={{
                  padding: ".85rem 1rem",
                  borderBottom: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                  <i
                    className="fa-solid fa-calendar-week"
                    style={{
                      color: "var(--blue)",
                      fontSize: ".82rem",
                      marginRight: ".35rem",
                    }}
                  />
                  This Week's Interviews
                </h4>
                <span
                  style={{
                    fontSize: ".76rem",
                    fontWeight: 600,
                    color: "var(--blue)",
                  }}
                >
                  {weekTotal} total
                </span>
              </div>
              <div
                style={{
                  padding: ".85rem 1rem",
                  display: "flex",
                  gap: ".5rem",
                  flexWrap: "wrap",
                }}
              >
                {weekInterviews.map(({ count, day, isToday }) => (
                  <div
                    key={day}
                    style={{
                      textAlign: "center",
                      padding: ".5rem .7rem",
                      border: `1.5px solid ${isToday ? "var(--primary)" : "var(--border)"}`,
                      borderRadius: 9,
                      minWidth: 48,
                      background: isToday ? "var(--primary-lt)" : "transparent",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: day === "Sat" ? "var(--gray)" : "var(--primary)",
                      }}
                    >
                      {count}
                    </div>
                    <div
                      style={{
                        fontSize: ".66rem",
                        color: isToday ? "var(--primary)" : "var(--muted)",
                        fontWeight: isToday ? 700 : 600,
                      }}
                    >
                      {day}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Offers in Flight */}
            <div className="og-card">
              <div
                style={{
                  padding: ".85rem 1rem",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                  <i
                    className="fa-solid fa-file-contract"
                    style={{
                      color: "var(--green)",
                      fontSize: ".82rem",
                      marginRight: ".35rem",
                    }}
                  />
                  Offers in Flight
                </h4>
              </div>
              <div
                style={{
                  padding: ".85rem 1rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: ".6rem",
                }}
              >
                {offersInFlight.map((o, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".75rem",
                      padding: ".65rem .9rem",
                      background: o.Bg,
                      borderRadius: 8,
                      border: o.Border,
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: o.AvatarColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: ".7rem",
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {o.Initials}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: ".85rem", fontWeight: 600 }}>
                        {o.Name}
                      </div>
                      <div style={{ fontSize: ".74rem", color: "var(--muted)" }}>
                        {o.Sub}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: ".7rem",
                        fontWeight: 700,
                        background: o.StatusBg,
                        color: o.StatusColor,
                        padding: "2px 7px",
                        borderRadius: 20,
                      }}
                    >
                      {o.StatusLabel}
                    </span>
                    <button
                      className="btn-ghost"
                      onClick={() =>
                        showToast(o.ToastMsg, o.Overdue ? "warn" : undefined)
                      }
                      title="Send reminder"
                      style={{
                        fontSize: ".8rem",
                        color: o.Overdue ? "var(--red)" : undefined,
                      }}
                    >
                      <i className="fa-solid fa-bell" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Activity Feed + Quick Actions ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 260px",
            gap: "1rem",
          }}
        >
          {/* Recent Activity */}
          <div className="og-card">
            <div
              style={{
                padding: ".85rem 1rem",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                <i
                  className="fa-solid fa-clock-rotate-left"
                  style={{
                    color: "var(--primary)",
                    fontSize: ".82rem",
                    marginRight: ".35rem",
                  }}
                />
                Recent Activity
              </h4>
              <button
                className="btn btn-outline btn-xs"
                onClick={() => showToast("Loading full activity log…")}
              >
                View All
              </button>
            </div>

            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}
            >
              {/* Left Column */}
              <div>
                {STATIC.activityLeft.map((it, idx) => (
                  <div className="feed-item" key={idx}>
                    <div className="fi-dot" style={{ background: it.Dot }}>
                      <i
                        className={`fa-solid ${it.Icon}`}
                        style={{ fontSize: ".5rem" }}
                      />
                    </div>
                    <div>
                      <div className="fi-text">
                        <strong>{it.Html[0]}</strong>
                        {it.Html[1]}
                      </div>
                      <div className="fi-time">{it.Time}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Column */}
              <div>
                {STATIC.activityRight.map((it, idx) => (
                  <div className="feed-item" key={idx}>
                    <div className="fi-dot" style={{ background: it.Dot }}>
                      <i
                        className={`fa-solid ${it.Icon}`}
                        style={{ fontSize: ".5rem" }}
                      />
                    </div>
                    <div>
                      <div className="fi-text">
                        <strong>{it.Html[0]}</strong>
                        {it.Html[1]}
                      </div>
                      <div className="fi-time">{it.Time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions + This Month */}
          <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
            {/* Quick Actions */}
            <div className="og-card">
              <div
                style={{
                  padding: ".85rem 1rem",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                  Quick Actions
                </h4>
              </div>
              <div style={{ padding: ".75rem" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: ".5rem",
                  }}
                >
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ justifyContent: "center", width: "100%" }}
                    onClick={() => navigate("recruitment-cv-upload.html")}
                  >
                    <i className="fa-solid fa-cloud-arrow-up" /> Upload CV / Add
                    Candidate
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: "center", width: "100%" }}
                    onClick={() => navigate("recruitment-job-create.html")}
                  >
                    <i className="fa-solid fa-briefcase" /> Create New Job
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: "center", width: "100%" }}
                    onClick={() =>
                      navigate("recruitment-interview-schedule.html")
                    }
                  >
                    <i className="fa-solid fa-calendar-plus" /> Schedule Interview
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: "center", width: "100%" }}
                    onClick={() =>
                      navigate("recruitment-talent-request-form.html")
                    }
                  >
                    <i className="fa-solid fa-clipboard-list" /> Raise Talent
                    Request
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ justifyContent: "center", width: "100%" }}
                    onClick={() => navigate("recruitment-link-candidate.html")}
                  >
                    <i className="fa-solid fa-link" /> Link Candidate to Job
                  </button>
                </div>
              </div>
            </div>

            {/* This Month */}
            <div className="og-card">
              <div
                style={{
                  padding: ".85rem 1rem",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <h4 style={{ margin: 0, fontSize: ".9rem", fontWeight: 700 }}>
                  This Month
                </h4>
              </div>
              <div
                style={{
                  padding: ".75rem .85rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: ".5rem",
                }}
              >
                {[
                  { label: "Candidates added", value: monthStats.CandidatesAdded, color: null },
                  { label: "Interviews conducted", value: monthStats.InterviewsScheduled, color: null },
                  { label: "Offers given", value: monthStats.OffersGiven, color: null },
                  { label: "Hires confirmed", value: monthStats.HiresConfirmed, color: "var(--green)" },
                  { label: "Avg. days to offer", value: monthStats.AvgDaysToOffer, color: null },
                ].map(({ label, value, color }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: ".82rem",
                    }}
                  >
                    <span style={{ color: "var(--muted)" }}>{label}</span>
                    <strong style={color ? { color } : {}}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* </div> */}

      {/* ── Feedback Modal ── */}
      {feedbackModal && (
        <FeedbackModal
          candidateName={feedbackModal}
          onClose={() => setFeedbackModal(null)}
        />
      )}
    </>
  );
}